<template>
  <div class="task-stage-page" data-qid="task-client-view">
    <b-alert v-if="error && error.length"
             show
             variant="danger"
             dismissible
             @dismissed="error = ''"
    >{{ error }}</b-alert>

    <!-- What the auditor asks for -->
    <!-- Type two: the request for the period (OE testing) follows in the same box under its own header -->
    <div class="task-control" v-if="requestText || requestOeText || requestFiles.length || requestLinks.length">
      <div class="task-control__label">{{ $t('tasks.v2.request') }}</div>
      <div class="task-control__text text-formatted" v-if="requestText">{{ requestText }}</div>
      <template v-if="requestOeText">
        <div class="task-control__label task-control__label--oe">
          <font-awesome-icon icon="calendar-alt" class="mr-1"/>{{ $t('tasks.v2.request_oe') }}
        </div>
        <div class="task-control__text text-formatted">{{ requestOeText }}</div>
      </template>
      <div class="task-request__files" v-if="requestFiles.length || requestLinks.length">
        <span v-for="file in requestFiles"
              :key="'request-file-' + file.id"
              class="task-request__file"
              @click="downloadMedia(file)"
        >
          <font-awesome-icon icon="paperclip" class="mr-1"/>{{ file.file_name }}
        </span>
        <a v-for="(link, index) in requestLinks"
           :key="'request-link-' + index"
           :href="link.url"
           target="_blank"
           class="task-request__file"
        >
          <font-awesome-icon icon="link" class="mr-1"/>{{ link.name || link.url }}
        </a>
      </div>
    </div>

    <!-- During OE testing only the tests can be worked on; the answer and files can still be read -->
    <div v-if="oeLocked" class="task-next" data-qid="data-collection-locked-notice">
      <font-awesome-icon icon="lock" class="mr-1"/>
      <span class="task-next__text">{{ $t('tasks.test_phase.data_collection_locked') }}</span>
    </div>

    <!-- The client's answer: the response and the comment -->
    <section v-if="hasResponse && !hasComment" class="v2-field" data-qid="task-client-response">
      <header class="v2-field__head"><h4>{{ $t('tasks.v2.your_answer') }}</h4></header>
      <EngagementTaskClientResponse/>
    </section>
    <EngagementTaskTextCard v-if="hasComment"
                            field="data_remarks"
                            :title="$t('tasks.v2.your_answer')"
                            :required="selectedTask.data_collection_add_description === 'Mandatory'"
                            :placeholder="$t('tasks.v2.answer_task_placeholder')"
                            :locked="readOnly"
    >
      <EngagementTaskClientResponse v-if="hasResponse"/>
    </EngagementTaskTextCard>

    <EngagementTaskDocuments client/>

    <!-- Tests (samples): during OE testing the client answers here -->
    <section v-if="tests.length" class="v2-field v2-field--flush" data-qid="task-client-tests">
      <header class="v2-field__head">
        <h4>{{ $t('testing.title') }}</h4>
        <span class="v2-field__meta">{{ $t('tasks.v2.tests_answered', { done: answered, total: tests.length }) }}</span>
      </header>
      <div v-for="(test, index) in tests"
           :key="'client-test-' + test.id"
           class="task-file task-file--test"
           @click="openTestModal(test, 'data')"
           data-qid="task-client-test"
      >
        <span class="task-file__no">{{ index + 1 }}</span>
        <div class="task-file__text">
          <b>{{ test.name }}</b>
          <div class="task-file__sub" v-if="test.description">{{ test.description }}</div>
        </div>
        <span class="v2-tag" :class="statusOf(test).cls">{{ statusOf(test).text }}</span>
        <font-awesome-icon icon="chevron-right" class="v2-tests__chevron"/>
      </div>
    </section>
  </div>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import EngagementTaskTextCard from './EngagementTaskTextCard.vue';
import EngagementTaskDocuments from './EngagementTaskDocuments.vue';
import EngagementTaskClientResponse from './EngagementTaskClientResponse.vue';

// The documentation collection task in the task panel: what is asked, the client's answer, files and tests
export default {
  name: 'EngagementTaskClientView',
  mixins: [assessmentSingleTaskProxy],
  components: {
    EngagementTaskTextCard,
    EngagementTaskDocuments,
    EngagementTaskClientResponse
  },
  computed: {
    requestText() {
      return this.selectedTask.data_collection_description;
    },
    // Only type two projects have OE testing
    requestOeText() {
      return this.usesOeTesting ? this.selectedTask.data_collection_description_two : '';
    },
    requestFiles() {
      return this.getMedia('data_collection');
    },
    requestLinks() {
      return this.getLinks('data_collection') || [];
    },
    hasResponse() {
      return this.selectedTask.add_response_doc === 'Optional' || this.selectedTask.add_response_doc === 'Mandatory';
    },
    hasComment() {
      return this.selectedTask.data_collection_add_description === 'Optional' || this.selectedTask.data_collection_add_description === 'Mandatory';
    },
    oeLocked() {
      return this.isInOeTesting && this.selectedTask.status !== 'completed';
    },
    readOnly() {
      return this.oeLocked || this.dataCollectionLocked || this.projectLocked || this.selectedTask.data_status === 'completed';
    },
    answered() {
      return _.filter(this.tests, test => test.status !== 'pending').length;
    }
  },
  methods: {
    statusOf(test) {
      const statuses = {
        pending: { cls: 'v2-tag--amber', text: this.$t('tasks.v2.test_status.client_todo') },
        documentation_collection_completed: { cls: 'v2-tag--violet', text: this.$t('tasks.v2.test_status.client_sent') },
        // The auditor's result isn't shown to the client: concluded tests stay "Sent"
        passed: { cls: 'v2-tag--violet', text: this.$t('tasks.v2.test_status.client_sent') },
        failed: { cls: 'v2-tag--violet', text: this.$t('tasks.v2.test_status.client_sent') }
      };
      return statuses[test.status] || statuses.pending;
    }
  }
};
</script>
